import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface WeakTopic {
  subject: string;
  topic: string;
  weaknessScore: number;
  accuracy: number;
  averageTime: number;
  reason: string;
}

interface Recommendation {
  recommendedMode: string;
  weakTopics: WeakTopic[];
  suggestedTopic?: {
    subject: string;
    topic: string;
    difficulty: string;
  };
  message: string;
}

const SmartPracticePage = () => {
  const navigate = useNavigate();
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedMode, setSelectedMode] = useState<string>('');

  useEffect(() => {
    fetchRecommendation();
  }, []);

  const fetchRecommendation = async () => {
    try {
      const response = await api.get('/practice/recommendation');
      setRecommendation(response.data.data);
      setSelectedMode(response.data.data.recommendedMode);
    } catch (error) {
      console.error('Failed to fetch recommendation:', error);
    } finally {
      setLoading(false);
    }
  };

  const startPractice = async (mode: string, subject?: string, topic?: string, difficulty?: string) => {
    try {
      const response = await api.post('/practice/start', {
        mode,
        subject,
        topic,
        difficulty,
        questionCount: 15,
      });

      // Navigate to practice interface with session data
      navigate('/practice/session', {
        state: {
          sessionId: response.data.data.sessionId,
          questions: response.data.data.questions,
          mode: response.data.data.mode,
        },
      });
    } catch (error) {
      console.error('Failed to start practice:', error);
      alert('Failed to start practice session. Please try again.');
    }
  };

  const handleQuickStart = () => {
    if (recommendation?.suggestedTopic) {
      startPractice(
        selectedMode,
        recommendation.suggestedTopic.subject,
        recommendation.suggestedTopic.topic,
        recommendation.suggestedTopic.difficulty
      );
    } else {
      startPractice(selectedMode);
    }
  };

  const getModeIcon = (mode: string) => {
    const icons: Record<string, string> = {
      weak_topic: '🎯',
      mistake_revision: '🔄',
      speed_training: '⚡',
      concept_builder: '📚',
      mixed_adaptive: '🎲',
    };
    return icons[mode] || '📝';
  };

  const getModeName = (mode: string) => {
    const names: Record<string, string> = {
      weak_topic: 'Weak Topic Practice',
      mistake_revision: 'Mistake Revision',
      speed_training: 'Speed Training',
      concept_builder: 'Concept Builder',
      mixed_adaptive: 'Mixed Adaptive',
    };
    return names[mode] || mode;
  };

  const getModeDescription = (mode: string) => {
    const descriptions: Record<string, string> = {
      weak_topic: 'Focus on your weakest topics with progressive difficulty',
      mistake_revision: 'Practice questions similar to your past mistakes',
      speed_training: 'Improve your solving speed with easy-medium questions',
      concept_builder: 'Build strong foundations with step-by-step learning',
      mixed_adaptive: 'AI adapts difficulty based on your performance',
    };
    return descriptions[mode] || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Analyzing your performance...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Smart Practice</h1>
        <p className="text-gray-600 mt-2">AI-powered personalized practice for JEE preparation</p>
      </div>

      {/* AI Recommendation Card */}
      {recommendation && (
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white mb-8">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold mb-2">🤖 AI Recommendation</h2>
              <p className="text-blue-100 mb-4">{recommendation.message}</p>
              {recommendation.suggestedTopic && (
                <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-4">
                  <p className="text-sm font-medium">Suggested Focus:</p>
                  <p className="text-lg font-bold">
                    {recommendation.suggestedTopic.topic} ({recommendation.suggestedTopic.subject})
                  </p>
                  <p className="text-sm">Difficulty: {recommendation.suggestedTopic.difficulty}</p>
                </div>
              )}
              <button
                onClick={handleQuickStart}
                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Start Recommended Practice →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Weak Topics Section */}
      {recommendation && recommendation.weakTopics.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">📊 Your Weak Topics</h2>
          <div className="space-y-3">
            {recommendation.weakTopics.map((topic, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{index + 1}</span>
                    <div>
                      <h3 className="font-semibold text-gray-900">{topic.topic}</h3>
                      <p className="text-sm text-gray-600 capitalize">{topic.subject}</p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-sm">
                    <span className="text-red-600">⚠️ {topic.reason}</span>
                    <span className="text-gray-600">Accuracy: {topic.accuracy.toFixed(1)}%</span>
                    <span className="text-gray-600">Avg Time: {Math.round(topic.averageTime)}s</span>
                  </div>
                </div>
                <button
                  onClick={() => startPractice('weak_topic', topic.subject, topic.topic, 'easy')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Practice Now
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Practice Modes */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">🎯 Choose Practice Mode</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {['weak_topic', 'mistake_revision', 'speed_training', 'concept_builder', 'mixed_adaptive'].map((mode) => (
            <div
              key={mode}
              onClick={() => setSelectedMode(mode)}
              className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
                selectedMode === mode
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
            >
              <div className="text-4xl mb-3">{getModeIcon(mode)}</div>
              <h3 className="font-semibold text-gray-900 mb-2">{getModeName(mode)}</h3>
              <p className="text-sm text-gray-600 mb-4">{getModeDescription(mode)}</p>
              {selectedMode === mode && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    startPractice(mode);
                  }}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Start Practice
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-3xl mb-2">🔥</div>
          <h3 className="text-lg font-semibold text-gray-900">Practice Streak</h3>
          <p className="text-3xl font-bold text-blue-600">0 days</p>
          <p className="text-sm text-gray-600 mt-1">Keep practicing daily!</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-3xl mb-2">⭐</div>
          <h3 className="text-lg font-semibold text-gray-900">Total XP</h3>
          <p className="text-3xl font-bold text-purple-600">0 XP</p>
          <p className="text-sm text-gray-600 mt-1">Earn XP by practicing</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-3xl mb-2">🎯</div>
          <h3 className="text-lg font-semibold text-gray-900">Topics Mastered</h3>
          <p className="text-3xl font-bold text-green-600">0 / 30</p>
          <p className="text-sm text-gray-600 mt-1">Master all topics!</p>
        </div>
      </div>
    </div>
  );
};

export default SmartPracticePage;
